import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DatePicker, Spin } from 'antd'
import dayjs from 'dayjs'
import ReactApexChart from 'react-apexcharts'
import {
	ChartPie,
	Ban,
	Banknote,
	FileText,
	CreditCard,
	RefreshCw,
	TriangleAlert,
	Wallet,
} from 'lucide-react'
import DemoShell from './components/DemoShell'
import { Card } from './components/Field'
import { InvoiceStatusBadge, SaleTypeBadge } from './components/Badges'
import { EmptyRow, TableWrap, Td, Th } from './components/Table'
import { getInvoices } from './api'
import { DATE_FORMAT, DEMO_INVOICES, demoInvoicePath } from './constants'
import {
	formatDate,
	formatMoney,
	getApiErrorMessage,
	getInvoiceStatus,
	isUnauthorized,
	toNumber,
} from './utils'
import { CHART_COLORS, baseChartOptions, compactNumber } from './charts'

const { RangePicker } = DatePicker

// Backend `totalCount` qaytarmaydi va agregat endpoint yo'q — shuning uchun
// statistika oxirgi MAX_PAGES × PAGE_SIZE ta hujjat ustidan hisoblanadi.
const PAGE_SIZE = 100
const MAX_PAGES = 5

const KpiCard = ({ icon: Icon, label, value, hint, tone = 'slate' }) => {
	const tones = {
		slate: 'bg-slate-100 text-slate-600',
		indigo: 'bg-indigo-50 text-indigo-600',
		emerald: 'bg-emerald-50 text-emerald-600',
		amber: 'bg-amber-50 text-amber-600',
		sky: 'bg-sky-50 text-sky-600',
		rose: 'bg-rose-50 text-rose-600',
	}
	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-4">
			<div className="flex items-center justify-between">
				<span className="text-[11px] uppercase tracking-wide text-slate-400">
					{label}
				</span>
				<span
					className={`flex h-8 w-8 items-center justify-center rounded-lg ${tones[tone]}`}
				>
					<Icon size={15} />
				</span>
			</div>
			<p className="mt-3 truncate text-[22px] font-semibold text-slate-900">
				{value}
			</p>
			{hint && <p className="mt-1 text-[12px] text-slate-400">{hint}</p>}
		</div>
	)
}

const DemoDashboard = () => {
	const navigate = useNavigate()

	const [range, setRange] = useState([dayjs().startOf('month'), dayjs()])
	const [rows, setRows] = useState([])
	const [canceledCount, setCanceledCount] = useState(0)
	const [truncated, setTruncated] = useState(false)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	const startDate = range?.[0]?.format(DATE_FORMAT)
	const endDate = range?.[1]?.format(DATE_FORMAT)

	const load = useCallback(
		(signal) => {
			setLoading(true)
			setError('')

			const baseParams = { startDate, endDate, pageSize: PAGE_SIZE }
			// TZ 1.2: `skip` — sahifa raqami, shuning uchun sahifalarni parallel so'raymiz
			const pageRequests = Array.from({ length: MAX_PAGES }, (_, index) =>
				getInvoices({ ...baseParams, skip: index, canceled: 'N' }, { signal }),
			)

			Promise.all([
				Promise.all(pageRequests),
				getInvoices({ ...baseParams, skip: 0, canceled: 'Y' }, { signal }).catch(
					() => [],
				),
			])
				.then(([pages, canceled]) => {
					if (signal?.aborted) return
					const unique = new Map()
					pages.flat().forEach((invoice) => unique.set(invoice.docEntry, invoice))
					setRows([...unique.values()])
					setTruncated(pages[pages.length - 1].length === PAGE_SIZE)
					setCanceledCount(canceled.length)
				})
				.catch((err) => {
					if (signal?.aborted) return
					if (isUnauthorized(err)) {
						navigate('/login', { replace: true })
						return
					}
					setRows([])
					setError(getApiErrorMessage(err))
				})
				.finally(() => {
					if (!signal?.aborted) setLoading(false)
				})
		},
		[startDate, endDate, navigate],
	)

	useEffect(() => {
		const controller = new AbortController()
		load(controller.signal)
		return () => controller.abort()
	}, [load])

	const stats = useMemo(() => {
		const totals = rows.reduce(
			(acc, invoice) => {
				acc.docTotal += toNumber(invoice.docTotal)
				acc.paid += toNumber(invoice.paidToDate)
				acc.open += toNumber(invoice.openBalance)
				if (invoice.saleType === 'credit') {
					acc.creditCount += 1
					acc.creditTotal += toNumber(invoice.docTotal)
				} else {
					acc.cashCount += 1
					acc.cashTotal += toNumber(invoice.docTotal)
				}
				const status = getInvoiceStatus(invoice)
				acc.status[status] = (acc.status[status] || 0) + 1
				return acc
			},
			{
				docTotal: 0,
				paid: 0,
				open: 0,
				cashCount: 0,
				cashTotal: 0,
				creditCount: 0,
				creditTotal: 0,
				status: {},
			},
		)

		const byDay = new Map()
		rows.forEach((invoice) => {
			const day = dayjs(invoice.docDate).format(DATE_FORMAT)
			const bucket = byDay.get(day) || { total: 0, paid: 0 }
			bucket.total += toNumber(invoice.docTotal)
			bucket.paid += toNumber(invoice.paidToDate)
			byDay.set(day, bucket)
		})
		const days = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))

		const bySeller = new Map()
		rows.forEach((invoice) => {
			const name = invoice.slpName || '—'
			bySeller.set(name, (bySeller.get(name) || 0) + toNumber(invoice.docTotal))
		})
		const topSellers = [...bySeller.entries()]
			.sort((a, b) => b[1] - a[1])
			.slice(0, 8)

		const byWarehouse = new Map()
		rows.forEach((invoice) => {
			;(invoice.documentLines || []).forEach((line) => {
				const name = line.whsName || line.warehouseCode || '—'
				byWarehouse.set(
					name,
					(byWarehouse.get(name) || 0) + toNumber(line.lineTotal),
				)
			})
		})
		const topWarehouses = [...byWarehouse.entries()]
			.sort((a, b) => b[1] - a[1])
			.slice(0, 6)

		return { totals, days, topSellers, topWarehouses }
	}, [rows])

	const { totals, days, topSellers, topWarehouses } = stats
	const count = rows.length
	const avgCheck = count ? totals.docTotal / count : 0
	const paidPercent = totals.docTotal
		? Math.round((totals.paid / totals.docTotal) * 100)
		: 0

	const recent = useMemo(
		() =>
			[...rows]
				.sort((a, b) => (a.docDate < b.docDate ? 1 : -1))
				.slice(0, 8),
		[rows],
	)

	const salesSeries = [
		{ name: 'Savdo', data: days.map(([, value]) => Number(value.total.toFixed(2))) },
		{
			name: 'To‘langan',
			data: days.map(([, value]) => Number(value.paid.toFixed(2))),
		},
	]

	const salesOptions = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'area' },
		colors: [CHART_COLORS.primary, CHART_COLORS.success],
		stroke: { curve: 'smooth', width: 2 },
		fill: {
			type: 'gradient',
			gradient: { opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 100] },
		},
		xaxis: {
			...baseChartOptions.xaxis,
			categories: days.map(([day]) => dayjs(day).format('DD.MM')),
			tickAmount: Math.min(days.length, 10),
		},
		tooltip: { shared: true, y: { formatter: (value) => formatMoney(value) } },
	}

	const saleTypeOptions = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'donut' },
		labels: ['Naqd', 'Nasiya'],
		colors: [CHART_COLORS.info, CHART_COLORS.primary],
		stroke: { width: 2, colors: ['#ffffff'] },
		dataLabels: {
			enabled: true,
			formatter: (percent) => `${Math.round(percent)}%`,
			style: { fontSize: '11px', fontWeight: 600 },
			dropShadow: { enabled: false },
		},
		plotOptions: {
			pie: {
				donut: {
					size: '68%',
					labels: {
						show: true,
						total: {
							show: true,
							label: 'Jami',
							color: '#64748B',
							fontSize: '12px',
							formatter: () => compactNumber(totals.docTotal),
						},
					},
				},
			},
		},
		tooltip: { y: { formatter: (value) => formatMoney(value) } },
	}

	const statusLabels = ['Yopilgan', 'Qarz bor', 'Ochiq']
	const statusSeries = [
		totals.status.closed || 0,
		totals.status.debt || 0,
		totals.status.open || 0,
	]
	const statusOptions = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'donut' },
		labels: statusLabels,
		colors: [CHART_COLORS.success, CHART_COLORS.warning, CHART_COLORS.info],
		stroke: { width: 2, colors: ['#ffffff'] },
		// amber↔emerald juftligi uchun majburiy direct-label
		dataLabels: {
			enabled: true,
			formatter: (percent, opts) => `${opts.w.config.series[opts.seriesIndex]}`,
			style: { fontSize: '11px', fontWeight: 600 },
			dropShadow: { enabled: false },
		},
		plotOptions: { pie: { donut: { size: '68%' } } },
		tooltip: { y: { formatter: (value) => `${value} ta` } },
	}

	const sellerOptions = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'bar' },
		colors: [CHART_COLORS.primary],
		plotOptions: {
			bar: { horizontal: true, borderRadius: 4, barHeight: '58%' },
		},
		xaxis: {
			...baseChartOptions.xaxis,
			categories: topSellers.map(([name]) => name),
		},
		yaxis: { labels: { style: { colors: '#475569', fontSize: '12px' } } },
		tooltip: { y: { formatter: (value) => formatMoney(value) } },
		legend: { show: false },
	}

	const warehouseOptions = {
		...baseChartOptions,
		chart: { ...baseChartOptions.chart, type: 'bar' },
		colors: [CHART_COLORS.info],
		plotOptions: {
			bar: { borderRadius: 4, columnWidth: '46%' },
		},
		xaxis: {
			...baseChartOptions.xaxis,
			categories: topWarehouses.map(([name]) => name),
		},
		tooltip: { y: { formatter: (value) => formatMoney(value) } },
		legend: { show: false },
	}

	return (
		<DemoShell
			title="Dashboard"
			subtitle="A/R Invoices statistikasi — SAP maʼlumotlari asosida"
			actions={
				<div className="flex flex-wrap items-center gap-2">
					<RangePicker
						value={range}
						format={DATE_FORMAT}
						allowClear={false}
						onChange={(value) => value && setRange(value)}
					/>
					<button
						type="button"
						onClick={() => load()}
						className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:border-slate-300"
					>
						<RefreshCw size={15} /> Yangilash
					</button>
				</div>
			}
		>
			{error && (
				<div className="mb-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
					<TriangleAlert size={18} className="mt-0.5 shrink-0" />
					<p className="font-medium">{error}</p>
				</div>
			)}

			<Spin spinning={loading}>
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
					<KpiCard
						icon={FileText}
						tone="indigo"
						label="Hujjatlar"
						value={count}
						hint={truncated ? `Birinchi ${MAX_PAGES * PAGE_SIZE} ta` : 'Tanlangan davr'}
					/>
					<KpiCard
						icon={Wallet}
						tone="slate"
						label="Umumiy savdo"
						value={formatMoney(totals.docTotal, { fallback: '0' })}
						hint={`O‘rtacha chek: ${formatMoney(avgCheck, { fallback: '0' })}`}
					/>
					<KpiCard
						icon={Banknote}
						tone="emerald"
						label="To‘langan"
						value={formatMoney(totals.paid, { fallback: '0' })}
						hint={`${paidPercent}% qoplangan`}
					/>
					<KpiCard
						icon={CreditCard}
						tone="amber"
						label="Qolgan qarz"
						value={formatMoney(totals.open, { fallback: '0' })}
						hint={`${totals.status.debt || 0} ta hujjatda`}
					/>
					<KpiCard
						icon={ChartPie}
						tone="sky"
						label="Naqd / Nasiya"
						value={`${totals.cashCount} / ${totals.creditCount}`}
						hint="Hujjatlar soni"
					/>
					<KpiCard
						icon={Ban}
						tone="rose"
						label="Bekor qilingan"
						value={canceledCount >= PAGE_SIZE ? `${PAGE_SIZE}+` : canceledCount}
						hint="Tanlangan davrda"
					/>
				</div>

				<div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
					<Card
						title="Savdo dinamikasi"
						subtitle="Kunlik summa va tushgan to‘lovlar"
						className="xl:col-span-2"
					>
						{days.length === 0 ? (
							<p className="py-16 text-center text-slate-400">
								Tanlangan davrda maʼlumot yo‘q
							</p>
						) : (
							<ReactApexChart
								options={salesOptions}
								series={salesSeries}
								type="area"
								height={300}
							/>
						)}
					</Card>

					<Card title="Savdo turi" subtitle="Summa bo‘yicha ulush">
						{count === 0 ? (
							<p className="py-16 text-center text-slate-400">Maʼlumot yo‘q</p>
						) : (
							<ReactApexChart
								options={saleTypeOptions}
								series={[
									Number(totals.cashTotal.toFixed(2)),
									Number(totals.creditTotal.toFixed(2)),
								]}
								type="donut"
								height={300}
							/>
						)}
					</Card>
				</div>

				<div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
					<Card title="Top sotuvchilar" subtitle="Savdo summasi bo‘yicha">
						{topSellers.length === 0 ? (
							<p className="py-16 text-center text-slate-400">Maʼlumot yo‘q</p>
						) : (
							<ReactApexChart
								options={sellerOptions}
								series={[
									{
										name: 'Savdo',
										data: topSellers.map(([, value]) =>
											Number(value.toFixed(2)),
										),
									},
								]}
								type="bar"
								height={300}
							/>
						)}
					</Card>

					<Card title="Sklad bo‘yicha savdo" subtitle="Qatorlar summasi">
						{topWarehouses.length === 0 ? (
							<p className="py-16 text-center text-slate-400">Maʼlumot yo‘q</p>
						) : (
							<ReactApexChart
								options={warehouseOptions}
								series={[
									{
										name: 'Savdo',
										data: topWarehouses.map(([, value]) =>
											Number(value.toFixed(2)),
										),
									},
								]}
								type="bar"
								height={300}
							/>
						)}
					</Card>

					<Card title="Hujjatlar holati" subtitle="Soni bo‘yicha">
						{count === 0 ? (
							<p className="py-16 text-center text-slate-400">Maʼlumot yo‘q</p>
						) : (
							<ReactApexChart
								options={statusOptions}
								series={statusSeries}
								type="donut"
								height={300}
							/>
						)}
					</Card>
				</div>

				<Card
					title="So‘nggi hujjatlar"
					className="mt-4"
					right={
						<button
							type="button"
							onClick={() => navigate(DEMO_INVOICES)}
							className="text-[13px] font-medium text-indigo-600 hover:underline"
						>
							Barchasi →
						</button>
					}
				>
					<TableWrap minWidth="min-w-[820px]">
						<thead>
							<tr>
								<Th>№</Th>
								<Th>Sana</Th>
								<Th>Mijoz</Th>
								<Th>Sotuvchi</Th>
								<Th>Savdo turi</Th>
								<Th align="right">Summasi</Th>
								<Th>Holati</Th>
							</tr>
						</thead>
						<tbody>
							{recent.length === 0 && <EmptyRow colSpan={7} />}
							{recent.map((invoice) => (
								<tr
									key={invoice.docEntry}
									onClick={() => navigate(demoInvoicePath(invoice.docEntry))}
									className="cursor-pointer transition hover:bg-indigo-50/40"
								>
									<Td className="font-semibold text-slate-900">
										{invoice.docNum}
									</Td>
									<Td className="whitespace-nowrap">
										{formatDate(invoice.docDate)}
									</Td>
									<Td>
										<p className="max-w-[240px] truncate">{invoice.cardName}</p>
										<p className="text-[12px] text-slate-400">
											{invoice.customerPhone || invoice.cardCode}
										</p>
									</Td>
									<Td className="text-slate-600">{invoice.slpName || '—'}</Td>
									<Td>
										<SaleTypeBadge
											saleType={invoice.saleType}
											installments={invoice.installments}
										/>
									</Td>
									<Td className="whitespace-nowrap text-right font-medium">
										{formatMoney(invoice.docTotal)}
									</Td>
									<Td>
										<InvoiceStatusBadge invoice={invoice} />
									</Td>
								</tr>
							))}
						</tbody>
					</TableWrap>
				</Card>

			</Spin>
		</DemoShell>
	)
}

export default DemoDashboard
